import React from "react";
import { useSnapshot } from "valtio";
import { t } from "i18next";
import swal from "@sweetalert/with-react";
import { PictureIcon } from "@aureskonnect/react-ui";

import {
    setProjectFiles as setStoreImage,
    setUploadedFiles,
    generalConfigStore,
    setActiveStandbyScreenFile,
    setActiveStateScreenItems,
    setStandbyScreenFile,
    setStandbyStateScreenItems,
    setFiles,
} from "@store";
import { setIsLoading } from "../../store/project";

import { FlexboxGrid } from "@components/Common/FlexboxGrid";
import { TruncatedTextWrapper } from "@components/Uploader/TruncatedTextWrapper";

import "./index.css";

type UploaderPropsType = {
    uploadedFileTitle?: string;
    id?: string;
    activeLanguage?: string;
    disabled?: boolean;
    screenItemId?: string;
    screenItems?: StandbyScreenItemType[];
    setScreenItems?: (activeStateScreenItems: StandbyScreenItemType[]) => void;
};

export function Uploader({
    uploadedFileTitle,
    id,
    activeLanguage,
    disabled,
    screenItemId,
    screenItems,
    setScreenItems,
}: UploaderPropsType): JSX.Element {
    const validUploadedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/gif",
        "video/mp4",
        "video/webm",
        "image/webp",
    ];

    const acceptedInputTypes = validUploadedTypes?.join(",");

    const {
        project: { files: images },
        uploadedFiles,
        activeAdvertisingScreenTab,
    } = useSnapshot(generalConfigStore);

    async function handleUpdatingUploadDataOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        const fileTarget = event.target?.files;
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/kiosk/uploaded-files`;
        if (fileTarget !== null && id !== undefined) {
            const tailleTotaleEnMo = fileTarget[0].size / (1024 * 1024);

            console.log(tailleTotaleEnMo)
            if(tailleTotaleEnMo>50){
                return swal({
                    icon: "error",
                    content: <p>{t("File too large. Limit: 50 MB")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
            if (!validUploadedTypes.includes(fileTarget[0].type)) {
                return swal({
                    icon: "error",
                    content: <p>{t("File type is not valid")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            }

            const formData = new FormData();
            formData.append("file", (fileTarget as FileList)[0]);
            formData.append("files", JSON.stringify(images as ImageItemType[]));
            formData.append("fileId", id as string);
            formData.append("activeLanguage", activeLanguage as string);
            setIsLoading(true);
            await fetch(apiUrl, {
                body: formData,
                method: "PUT",
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.error) {
                        throw new Error(data.message);
                    }

                    const imagePath = `${URL.createObjectURL(fileTarget[0])}`;

                    const imagesArray = [...images];

                    const image = imagesArray.find(
                        (element) => element.id === id
                    );

                    if (image !== undefined) {
                        const imageIndex = imagesArray.indexOf(image);
                        imagesArray[imageIndex] = data.data;
                        if (id === "26a6a801-af51-46fb-b46d-4e4cfb1f604f") {
                            const modifiedLanguage = Object.keys(
                                data.data.content
                            ).reduce((prev: any, key: any) => {
                                prev[key] = {
                                    ...data.data.content[key],
                                    path:
                                        data.data.content[
                                            activeLanguage as string
                                        ].path,
                                };

                                return prev;
                            }, {});

                            imagesArray[imageIndex].content = {
                                ...modifiedLanguage,
                            };
                        }

                        const data1 = [...imagesArray];

                        if (typeof data1[imageIndex].content !== "string") {
                            data1[imageIndex] = {
                                ...data.data,
                                content: {
                                    ...data.data.content,
                                    [activeLanguage as string]: {
                                        ...data.data.content[
                                            activeLanguage as string
                                        ],
                                        path: imagePath,
                                    },
                                } as ImageItemLanguagesType,
                            };
                            if (id === "26a6a801-af51-46fb-b46d-4e4cfb1f604f") {
                                const modifiedLanguage = Object.keys(
                                    data.data.content
                                ).reduce((prev: any, key: any) => {
                                    prev[key] = {
                                        ...data.data.content[key],
                                        path: imagePath,
                                    };

                                    return prev;
                                }, {});

                                data1[imageIndex].content = {
                                    ...modifiedLanguage,
                                };
                            }
                        } else {
                            data1[imageIndex] = {
                                ...data.data,
                                content: imagePath,
                                type: data.data.type as string,
                            };
                        }

                        setFiles(data1);
                        setStoreImage(imagesArray);

                        const filesArray = [...uploadedFiles];
                        const searchedFile = filesArray.find(
                            (element) => element.id === id
                        );

                        if (searchedFile !== undefined) {
                            const fileIndex = filesArray.indexOf(
                                searchedFile as UploadedFilesType
                            );

                            if (activeLanguage === undefined) {
                                filesArray[fileIndex] = {
                                    id,
                                    uploadedFile: data.data,
                                };
                            } else {
                                const localContent =
                                    filesArray[fileIndex].uploadedFile;
                                filesArray[fileIndex] = {
                                    ...filesArray[fileIndex],
                                    uploadedFile: {
                                        ...localContent,
                                        [activeLanguage]: data.data,
                                    },
                                };
                            }
                        } else {
                            if (activeLanguage === undefined) {
                                filesArray.push({
                                    id,
                                    uploadedFile: data.data,
                                });
                            } else {
                                filesArray.push({
                                    id,
                                    uploadedFile: {
                                        [activeLanguage]: data.data,
                                    },
                                });
                            }
                        }

                        setUploadedFiles(filesArray);
                        if (screenItemId !== undefined) {
                            const uploadedFileIndex = filesArray.indexOf(
                                filesArray.find(
                                    (element) => element.id === screenItemId
                                ) as UploadedFilesType
                            );

                            let theLastUploadedFile: UploadedFilesType;

                            if (uploadedFileIndex === -1) {
                                theLastUploadedFile =
                                    filesArray[filesArray.length - 1];
                            } else {
                                theLastUploadedFile =
                                    filesArray[uploadedFileIndex];
                            }

                            const localData = [
                                ...(screenItems as StandbyScreenItemType[]),
                            ];
                            const itemIndex = localData.indexOf(
                                localData.find(
                                    (item) => item.id === theLastUploadedFile.id
                                ) as StandbyScreenItemType
                            );
                            let data1 = [...localData];
                            localData[itemIndex] = ({
                                ...localData[itemIndex],
                                content: (theLastUploadedFile.uploadedFile as LanguageUploadedFileType)
                                    .content,
                                timeout: (theLastUploadedFile.uploadedFile
                                    .type as string).includes("video")
                                    ? (theLastUploadedFile.uploadedFile as LanguageUploadedFileType)
                                          .timeout
                                    : 3,
                                type: theLastUploadedFile.uploadedFile.type,
                            } as unknown) as StandbyScreenItemType;

                            data1[itemIndex] = ({
                                ...data1[itemIndex],
                                content: `${URL.createObjectURL(
                                    fileTarget[0]
                                )}`,
                                timeout: (theLastUploadedFile.uploadedFile
                                    .type as string).includes("video")
                                    ? (theLastUploadedFile.uploadedFile as LanguageUploadedFileType)
                                          .timeout
                                    : 3,
                                type: theLastUploadedFile.uploadedFile.type,
                            } as unknown) as StandbyScreenItemType;

                            if (activeAdvertisingScreenTab === 0) {
                                setActiveStateScreenItems(localData);
                                setActiveStandbyScreenFile(data1);
                            } else {
                                setStandbyStateScreenItems(localData);
                                setStandbyScreenFile(data1);
                            }
                        }
                    }
                    setIsLoading(false);
                    return swal({
                        icon: "success",
                        content: <p>{t(data.message)}!</p>,
                        buttons: false,
                        timer: 2000,
                    });
                })
                .catch((error) => {
                    return (
                        setIsLoading(false),
                        swal({
                            icon: "error",
                            content: <p>{t(error.message)}</p>,
                            buttons: false,
                            timer: 2000,
                        })
                    );
                });
        }
    }

    return (
        <FlexboxGrid gap="5px" alignItemsCentered={true}>
            <label
                className="mb-0 d-flex align-items-center file-uploader__clz rounded"
                style={{
                    gap: "5px",
                    cursor:
                        disabled !== undefined && disabled === true
                            ? "not-allowed"
                            : "pointer",
                }}
            >
                <PictureIcon
                    style={{
                        height: "30px",
                        width: "30px",
                        fill:
                            disabled !== undefined && disabled === true
                                ? "#999a9a"
                                : "#323333",
                    }}
                />
                <input
                    accept={acceptedInputTypes}
                    multiple={false}
                    type="file"
                    title="input"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        handleUpdatingUploadDataOnChangeEvent(event);
                    }}
                    onClick={(event: any) => {
                        event.currentTarget.value = "";
                    }}
                    disabled={disabled}
                />
            </label>

            {uploadedFileTitle !== undefined ? (
                <TruncatedTextWrapper
                    imageId={id}
                    screenItemId={screenItemId}
                    screenItems={screenItems}
                />
            ) : null}
        </FlexboxGrid>
    );
}
